import { useEffect, useRef, useState, useCallback, useMemo, CSSProperties } from 'react';
import { setupIframeResize } from '../utils/iframeResize';

declare global {
  interface Window {
    ResizeObserver: typeof ResizeObserver;
  }
}

// Function to generate iframe HTML content with the resizing script
const getIframeContent = (elementType: string) => `
  <!DOCTYPE html>
  <html>
    <head>
      <base target="_parent" />
      <style>
        body { margin: 0; padding: 0; overflow: hidden; }
        #root { width: 100%; min-height: 100%; }
      </style>
      <script type="module">
        import { setupIframeContentResize } from '${window.location.origin}/elements/iframeResize.js';
        
        // Wait for the element to be mounted
        const init = () => {
          setupIframeContentResize({
            minHeight: 100,
            offset: 20,
            elementId: '${elementType}'
          });
        };
        
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', init);
        } else {
          init();
        }
      </script>
    </head>
    <body>
      <div id="root"></div>
      <script type="module" src="/elements/${elementType}.js"></script>
    </body>
  </html>
`;

export interface ElementModalProps {
  elementType: string;
  onClose: () => void;
  isOpen?: boolean;
  title?: string;
}

/**
 * ElementModal component
 * Renders an element in a modal window with a blurred backdrop
 */
const ElementModal = ({
  elementType,
  onClose,
  isOpen = true,
  title = 'Element'
}: ElementModalProps) => {
  const [iframeHeight, setIframeHeight] = useState<string>('auto');
  const modalRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Handle escape key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Handle click outside modal content to close
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  // Handle iframe resize
  const handleIframeResize = useCallback((height: number) => {
    console.log('Received iframe height:', height);
    
    // Validate height
    if (typeof height !== 'number' || isNaN(height) || height <= 0) {
      console.warn('Invalid height received from iframe:', height);
      return;
    }
    
    // Add some padding to the height (20px top and bottom)
    const padding = 40;
    const viewportHeight = window.innerHeight;
    const maxHeight = Math.min(viewportHeight * 0.9, 2000); // Cap at 2000px or 90% of viewport
    const newHeight = Math.min(Math.ceil(height) + padding, maxHeight);
    
    console.log('Setting iframe height:', {
      receivedHeight: height,
      padding,
      viewportHeight,
      maxHeight,
      newHeight
    });
    
    // Only update if the height has changed significantly
    const currentHeight = parseFloat(iframeHeight) || 0;
    if (Math.abs(newHeight - currentHeight) > 1) {
      setIframeHeight(`${newHeight}px`);
      
      // If this is the first time we're setting the height, trigger a re-check
      if (currentHeight === 0) {
        console.log('Initial height set, scheduling re-check');
        setTimeout(() => {
          if (iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.postMessage(
              { type: 'getHeight', elementId: elementType },
              '*'
            );
          }
        }, 500);
      }
    } else {
      console.log('Height change too small to update:', Math.abs(newHeight - currentHeight));
    }
  }, [elementType, iframeHeight]);

  // Set up iframe resize handling
  useEffect(() => {
    if (!iframeRef.current || !isOpen) return;

    console.log('Setting up iframe resize handling for element:', elementType);
    
    let cleanupResize: (() => void) | null = null;
    let timeoutId: Timeout | null = null;
    const maxRetries = 5;
    const retryDelay = 200; // ms

    const handleResizeMessage = (height: number) => {
      console.log('Handling resize message with height:', height);
      handleIframeResize(height);
    };

    const requestResize = () => {
      if (iframeRef.current?.contentWindow) {
        console.log('Requesting iframe resize for element:', elementType);
        iframeRef.current.contentWindow.postMessage(
          { 
            type: 'resize', 
            elementId: elementType,
            timestamp: Date.now() 
          },
          '*'
        );
      }
    };

    const setupResizeHandler = () => {
      if (!iframeRef.current) return;

      console.log('Setting up resize handler for element:', elementType);
      
      // Setup resize handler for the iframe
      cleanupResize = setupIframeResize(
        iframeRef.current,
        handleResizeMessage,
        elementType
      );

      // Initial resize check with increasing delays
      const scheduleResizeRequest = (attempt: number) => {
        if (attempt >= maxRetries) {
          console.warn('Max resize retries reached for element:', elementType);
          return;
        }

        const delay = attempt === 0 ? 100 : retryDelay * Math.pow(2, attempt - 1);
        console.log(`Scheduling resize request in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
        
        timeoutId = setTimeout(() => {
          if (iframeRef.current?.contentWindow) {
            requestResize();
            
            // Schedule next attempt if needed
            if (attempt < maxRetries - 1) {
              scheduleResizeRequest(attempt + 1);
            }
          }
        }, delay);
      };

      // Start the sequence
      scheduleResizeRequest(0);
    };

    const iframe = iframeRef.current;
    const handleLoad = () => {
      console.log('Iframe loaded, setting up resize handler');
      setupResizeHandler();
    };

    // Set up event listeners
    if (iframe) {
      iframe.addEventListener('load', handleLoad);
      
      // Initial check in case iframe is already loaded
      if (iframe.contentDocument?.readyState === 'complete') {
        console.log('Iframe already loaded, initializing resize handler');
        setupResizeHandler();
      }
    }

    // Cleanup function
    return () => {
      console.log('Cleaning up iframe resize handlers');
      
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      if (iframe) {
        iframe.removeEventListener('load', handleLoad);
      }
      
      if (cleanupResize) {
        console.log('Calling cleanupResize');
        cleanupResize();
      }
      
      // ResizeObserver is not currently used in this implementation
    };
  }, [elementType, isOpen, handleIframeResize]);

  if (!isOpen) {
    return null;
  }

  const iframeId = `element-iframe-${Date.now()}`;
  
  // Generate iframe content with the resizing script
  const iframeContent = useMemo(() => {
    return getIframeContent(elementType);
  }, [elementType]);

  // CSS styles using React's CSSProperties
  const modalOverlayStyles: CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
  };

  const modalContentStyles: CSSProperties = {
    position: 'relative',
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflow: 'auto',
  };

  const closeButtonStyles: CSSProperties = {
    position: 'absolute',
    top: '12px',
    right: '12px',
    background: 'transparent',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    zIndex: 1,
    padding: '4px 8px',
  };

  const iframeContainerStyles = (height: string): CSSProperties => ({
    padding: '24px',
    minHeight: '200px',
    height: height,
    overflow: 'hidden'
  });

  return (
    <div style={modalOverlayStyles} onClick={handleBackdropClick}>
      <div 
        style={modalContentStyles}
        ref={modalRef} 
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <button 
          style={closeButtonStyles}
          onClick={onClose} 
          aria-label="Close modal"
        >
          &times;
        </button>
        <div style={iframeContainerStyles(iframeHeight)}>
          <iframe
            ref={iframeRef}
            id={iframeId}
            title={title}
            srcDoc={iframeContent}
            onLoad={(e) => {
              console.log('Iframe onLoad event triggered', { 
                iframeId,
                elementType,
                readyState: (e.target as HTMLIFrameElement).contentDocument?.readyState 
              });
              
              // The resize logic is now handled by the useEffect hook
              // This is kept for backward compatibility
              const target = e.target as HTMLIFrameElement;
              if (target.contentWindow) {
                console.log('Sending initial resize request from onLoad');
                target.contentWindow.postMessage({ 
                  type: 'resize',
                  elementId: elementType,
                  source: 'onLoad',
                  timestamp: Date.now()
                }, '*');
              }
            }}
            style={{
              width: '100%',
              height: iframeHeight,
              border: 'none',
              overflow: 'hidden',
              opacity: iframeHeight === 'auto' ? 0 : 1,
              transition: 'opacity 0.2s ease-in-out'
            }}
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-same-origin"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
};

// Export as both default and named for better type support
export { ElementModal };
export default ElementModal;
